"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

function Table({
  className,
  disableContainerScroll = false,
  ...props
}: React.ComponentProps<"table"> & { disableContainerScroll?: boolean }) {
  return (
    <div
      data-slot="table-container"
      className={cn(
        "relative w-full",
        !disableContainerScroll && "overflow-x-auto",
      )}
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "[&_tr]:border-b [&_tr]:bg-muted/50 [&_tr]:hover:bg-muted/50",
        className,
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t font-medium [&>tr]:border-b-0 [&>tr]:bg-transparent [&>tr]:hover:bg-transparent",
        className,
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b hover:bg-muted/30 has-aria-expanded:bg-muted/30 data-[state=selected]:bg-primary/[0.03] data-[state=selected]:hover:bg-primary/[0.05]",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, style, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-foreground [&:has([role=checkbox])]:pr-0",
        className
      )}
      style={style}
      {...props}
    />
  )
}

function TableCell({ className, style, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",

        "[&:has(>[data-slot=switch]:only-child)]:leading-[0]",
        "[&:has(>*:only-child>[data-slot=switch]:only-child)]:leading-[0]",
        className
      )}
      style={style}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
